import { useEffect, useRef, useState } from 'react';

import { IconButton, InputAdornment, styled, SxProps, Typography, useTheme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import VectorImage from '~components/media/vectorImage';
import { BaseReference } from '~interfaces';
import { Dimens } from '~themes';

export interface DropdownProps {
	value?: BaseReference | undefined;
	values: BaseReference[];
	canClear: boolean;
	placeholder?: string;
	onValueChanged: (newValue: BaseReference | null) => void;
	sx?: SxProps;
}

const Dropdown = ({ placeholder, value, ...props }: DropdownProps) => {
	const { palette } = useTheme();

	const [internalValue, setInternalValue] = useState<BaseReference | null>(value ?? null);
	const isControlled = value !== undefined;
	const wasControlledRef = useRef(isControlled);

	useEffect(() => {
		// When the parent stops passing a value (controlled → uncontrolled), clear the
		// stale internal state so the dropdown doesn't flash the old selection.
		if (wasControlledRef.current && !isControlled && internalValue !== null) {
			setInternalValue(null);
		}
		wasControlledRef.current = isControlled;
	}, [isControlled, internalValue]);

	const selectedValue = isControlled ? (value ?? null) : internalValue;
	const selectedValueId = selectedValue?.id ?? '';

	const handleChange = (event: SelectChangeEvent<string>) => {
		const newValue =
			props.values.find((dropdownValue) => {
				return dropdownValue.id === event.target.value;
			}) ?? null;

		if (!isControlled) {
			setInternalValue(newValue);
		}
		props.onValueChanged(newValue);
	};

	return (
		<StyledDropDown sx={{ minWidth: 200, ...props.sx }}>
			<Select
				value={selectedValueId}
				onChange={(value) => {
					handleChange(value);
				}}
				renderValue={(currentValue) => {
					if (currentValue === '' && placeholder != undefined) {
						return (
							<Typography variant='paragraph' color={palette.textPlaceholder.main}>
								{placeholder}
							</Typography>
						);
					}

					return (
						props.values.find((dropdownValue) => dropdownValue.id === currentValue)?.name ?? ''
					);
				}}
				IconComponent={(iconProps) => <VectorImage name='chevron/down' {...iconProps} />}
				endAdornment={
					props.canClear && selectedValueId ?
						<InputAdornment sx={{ right: '24px', position: 'absolute' }} position='end'>
							<IconButton
								onClick={() => {
									props.onValueChanged(null);
									if (!isControlled) {
										setInternalValue(null);
									}
								}}
							>
								<VectorImage name='icon/cross' />
							</IconButton>
						</InputAdornment>
					:	undefined
				}
				variant='outlined'
				displayEmpty
			>
				<StyledMenuItem value='' sx={{ display: 'none' }}>
					{placeholder ?? ''}
				</StyledMenuItem>
				{props.values.map((dropdownValue) => (
					<StyledMenuItem key={dropdownValue.id} value={dropdownValue.id}>
						{dropdownValue.name}
					</StyledMenuItem>
				))}
			</Select>
		</StyledDropDown>
	);
};

const StyledDropDown = styled(FormControl)(({ theme }) => ({
	'& .MuiInputBase': {
		'&-root': {
			padding: `${Dimens.spacingMd} ${Dimens.spacingLg} ${Dimens.spacingMd} ${Dimens.spacingLg}`,
			outline: 'none',
			'&:hover .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:focus .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:active .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
			'&:focus-visible .MuiOutlinedInput-notchedOutline': {
				border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
			},
		},
		'&-input': {
			...theme.typography.paragraph,
			color: theme.palette.textTertiary.main,
			padding: 0,
			minHeight: '24px',
			'&.MuiSelect-outlined': {
				paddingRight: 0,
			},
			'&::placeholder': {
				color: theme.palette.textPlaceholder.main,
				opacity: 1,
			},
		},
	},
	'& svg': {
		height: '16px',
		width: '16px',
		right: '12px',
		top: 'calc(50% - 8px)',
	},
	'& .MuiOutlinedInput': {
		'&-notchedOutline': {
			top: 0,
			border: `1px solid ${theme.palette.borderPrimary.main}`,
			'& legend': {
				// This is the mui label that places itself in the border.
				// But it leaves a gap in the border, so we hide it.
				display: 'none',
			},
		},
	},
	'&.MuiOutlinedInput': {
		'&-notchedOutline': {
			top: 0,
			border: `1px solid ${theme.palette.borderPrimary.main}`,
			'& legend': {
				// This is the mui label that places itself in the border.
				// But it leaves a gap in the border, so we hide it.
				display: 'none',
			},
		},
	},
	'& .MuiInputAdornment-root': {
		marginRight: Dimens.spacingMd,
	},
	'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
		borderColor: theme.palette.borderPrimarySolid.main + '!important',
		outline: 'none',
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: `2px solid ${theme.palette.borderPrimarySolid.main}`,
		borderColor: theme.palette.borderPrimarySolid.main + '!important',
		outline: 'none',
	},
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	...theme.typography.labelSm,
	color: theme.palette.textTertiary.main,
}));

export default Dropdown;
